import { useOutletContext } from "react-router-dom";
import { Alert, Button, ListItem, ListItemText } from "@mui/material";
import DOMPurify from 'dompurify';

import Page from "~/components/Page";
import { useTrans } from "~/hooks/useTrans";
import { getProjectBreadcrumb } from "~/utils/breadcrumbs";
import { apiGetProject, apiGetProjectCatalog, apiGetProjectCatalogGallery } from "~/providers/api";
import { IOutlet } from "~/interfaces";
import { IAdminProject } from "~/providers/admin-interfaces";
import { IClientProject, IClientProjectCatalog, IClientProjectCatalogGallery, IClientProjectCatalogPayload } from "~/providers/client-interfaces";
import Loader from '~/components/Loader';
import ErrorInfo from '~/components/ErrorInfo';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import PaperWithTitle from "~/components/PaperWithTitle";
import EditProjectCatalogModal from "~/modals/EditProjectCatalogModal";
import { classNames, getCatalogLink } from "~/utils/utils";
import EditProjectCatalogPublishModal from "~/modals/EditProjectCatalogPublishModal";
import AdminProjectCatalogModal from "~/modals/AdminProjectCatalogModal";
import EditProjectCatalogGalleryModal from "~/modals/EditProjectCatalogGalleryModal";
import ExternalLink from "~/components/ExternalLink";
import WhatAbout from "~/components/WhatAbout";

import "./style.less";

interface IProjectCatalogPage {
	isInternal?: boolean;
}

interface IState {
	loading: boolean;
	isDirty: boolean;
	errorMsg: string;
	project: IClientProject | IAdminProject;
	catalog: IClientProjectCatalog;
	gallery: Array<IClientProjectCatalogGallery>;
	showPublishDialog: boolean;
	showEditDialog: boolean;
	showPreviewsDialog: boolean;
	showAdminDialog: boolean;
	showUpdateInfo: boolean;
}

const STATUS_NOT_FOUND = 404;
const INIT_STATE: IState = {
	loading: true,
	isDirty: false,
	errorMsg: "",
	project: null,
	catalog: null,
	gallery: [],
	showPublishDialog: false,
	showEditDialog: false,
	showPreviewsDialog: false,
	showAdminDialog: false,
	showUpdateInfo: false,
};

export default function ProjectCatalogPage({
	isInternal = false,
}: IProjectCatalogPage) {
	const { trans, transCatalogApproved, transCatalogQuality } = useTrans();
	const { lang } = useLang();
	const { state, updateState } = myUseState<IState>(INIT_STATE);
	const { params }: IOutlet = useOutletContext();
	const title = trans("projectCatalogPage.title.public");

	async function loadData() {
		updateState(INIT_STATE);

		const [projectData, catalogData] = await Promise.all([
			apiGetProject(isInternal ? "admin" : "client", params.projectId, lang),
			apiGetProjectCatalog(isInternal ? "admin" : "client", params.projectId, lang),
		]);
		let galleryData: Array<IClientProjectCatalogGallery> = [];
		let galleryError: Array<string> = [];

		if (catalogData.data) {
			const catalogGallery = await apiGetProjectCatalogGallery(isInternal ? "admin" : "client", params.projectId, catalogData.data.id, lang);

			if (catalogGallery.noErrors) {
				galleryData = catalogGallery.data;
			} else {
				galleryError = catalogGallery.errors;
			}
		}

		updateState({
			loading: false,
			// castecna data
			...projectData.noErrors
				? {
					project: projectData.data,
				}
				: {},
			...catalogData.noErrors
				? {
					// seradime obracene
					catalog: catalogData.data,
				}
				: {},
			gallery: galleryData,
			errorMsg: [
				...projectData.errors,
				...catalogData.status === STATUS_NOT_FOUND
					? []
					: [catalogData.errors],
				...galleryError,
			].join(", "),
		});
	}

	function getCatalogProperty(name: keyof IClientProjectCatalogPayload, replaceLinesAndSanitaze = false) {
		if (state.catalog && state.catalog.payload.length > 0) {
			const payloadFiltered = state.catalog.payload.filter(item => item.language === lang);
			const payload = payloadFiltered.length
				? payloadFiltered[0]
				: state.catalog.payload[0];
			const data = payload[name];

			if (replaceLinesAndSanitaze) {
				return DOMPurify.sanitize(data.replaceAll("\n", "<br />"));
			}

			return data;
		}

		return "";
	}

	function onDialogClose() {
		updateState({
			showEditDialog: false,
			showPublishDialog: false,
			showAdminDialog: false,
			showPreviewsDialog: false,
			isDirty: false,
		});
	}

	function onEditDialogSave(catalog: IClientProjectCatalog) {
		updateState({
			catalog,
			showEditDialog: false,
			isDirty: false,
			showUpdateInfo: true,
		});
	}

	function onPublishSave(catalog: IClientProjectCatalog) {
		updateState({
			catalog,
			showPublishDialog: false,
			isDirty: false,
			showUpdateInfo: true,
		});
	}

	function onAdminDialogSave(catalog: IClientProjectCatalog) {
		updateState({
			catalog,
			showAdminDialog: false,
			isDirty: false,
			showUpdateInfo: true,
		});
	}

	function onPreviewsSave(gallery: Array<IClientProjectCatalogGallery>) {
		updateState({
			isDirty: false,
			showPreviewsDialog: false,
			gallery,
			showUpdateInfo: true,
		});
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			loadData();
		}
	}

	/* eslint-disable no-magic-numbers */
	return <Page checkLogged={true} title={title} breadcrumbs={getProjectBreadcrumb(trans, true, state.project, title)}
		checkInternalRole={isInternal ? "salesman" : "none"} onLoad={onPageLoad} isDirty={state.isDirty} seoTitleTrans="seo.title.settings">
		{ state.loading && <Loader msg={trans("loadingData")} /> }
		{ !state.loading && <div className="dashboardGrid">
			{ state.catalog && <>
				<PaperWithTitle title={ trans("projectCatalogPage.publishTitle") } onClick={() => updateState({ showPublishDialog: true })} className="projectCatalogPage__gridContent">
					<p className="projectCatalogPage__publishP">
						<span>{ trans("projectCatalogPage.publishInfo") }</span>
						<span className={classNames(["projectCatalogPage__publishInfo", state.catalog.published ? "green-text-color" : "red-text-color"])}>{
							trans(state.catalog.published ? "projectCatalogPage.publishInfo.yes" : "projectCatalogPage.publishInfo.no")
						}</span>
					</p>
					<WhatAbout title={ trans("projectCatalogPage.whatAbout") } link={getCatalogLink(lang)} />
				</PaperWithTitle>
				<PaperWithTitle title={ trans("projectCatalogPage.previews.title") } onClick={() => updateState({ showPreviewsDialog: true })}>
					<div className="projectCatalogPage__galleryPreviewsContent">
						{ state.gallery.length === 0 && <p>{ trans("projectCatalogPage.previews.empty") }</p> }
						{ state.gallery.length > 0 && <div className="projectCatalogPage__galleryPreviews">
							{ state.gallery.map(item => <div key={item.id} className="projectCatalogPage__galleryPreviewImageCover">
								<img className="projectCatalogPage__galleryPreviewImage" alt="" src={item.images.small} />
							</div>) }
						</div> }
					</div>
				</PaperWithTitle>
			</> }
			{ (!state.catalog && !isInternal) && <PaperWithTitle title={ trans("projectCatalogPage.create") } className="projectCatalogPage__gridContent">
				<Button color="success" variant="contained" onClick={() => updateState({ showEditDialog: true })} className="projectCatalogPage__createBtn">
					{ trans("projectCatalogPage.createBtn") }
				</Button>
				<WhatAbout title={ trans("projectCatalogPage.whatAbout") } link={getCatalogLink(lang)} />
			</PaperWithTitle> }
			{ (state.catalog || isInternal) && <PaperWithTitle title={ state.catalog ? trans("projectCatalogPage.settingsTitle") : trans("projectCatalogPage.createCatalogTitle") } onClick={() => updateState({ showEditDialog: true })}>
				<ListItem>
					<ListItemText primary={getCatalogProperty("title")} secondary={trans("projectCatalogPage.settings.title")} />
				</ListItem>
				<ListItem>
					<ListItemText primary={getCatalogProperty("subtitle")} secondary={trans("projectCatalogPage.settings.perex")} />
				</ListItem>
				<ListItem>
					<ListItemText primary={<span dangerouslySetInnerHTML={{ __html: getCatalogProperty("description", true) }} />} secondary={trans("projectCatalogPage.settings.description")} />
				</ListItem>
				<ListItem>
					<ListItemText primary={<span dangerouslySetInnerHTML={{ __html: getCatalogProperty("apiDescription", true) }} />} secondary={trans("projectCatalogPage.settings.apiDescription")} />
				</ListItem>
				<ListItem>
					<ListItemText primary={<ExternalLink to={state.catalog?.referenceUrl}>{ state.catalog?.referenceUrl || "" }</ExternalLink>} secondary={trans("projectCatalogPage.settings.url")} />
				</ListItem>
			</PaperWithTitle> }
			{ (isInternal && state.catalog) && <PaperWithTitle title={ trans("projectCatalogPage.admin.title") } onClick={() => updateState({ showAdminDialog: true })}>
				<ListItem>
					<ListItemText primary={transCatalogApproved(state.catalog.approvedByAdmin)} secondary={trans("adminProjectCatalogModal.approvedByAdmin.title")} />
				</ListItem>
				<ListItem>
					<ListItemText primary={transCatalogQuality(state.catalog.quality)} secondary={trans("adminProjectCatalogModal.quality.title")} />
				</ListItem>
			</PaperWithTitle> }
		</div> }
		{ state.showUpdateInfo && <Alert severity="info" className="margin-top-large">
			{ trans("projectCatalogPage.afterChange") }
		</Alert> }
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} sx={{ marginTop: "var(--margin-large)" }} /> }
		{ state.showEditDialog && <EditProjectCatalogModal isInternal={isInternal} catalogData={state.catalog} params={params} onChange={isDirty => updateState({ isDirty })}
			onSave={onEditDialogSave} onClose={onDialogClose} /> }
		{ state.showPublishDialog && <EditProjectCatalogPublishModal catalogData={state.catalog} isInternal={isInternal} params={params} onClose={onDialogClose}
			onSave={onPublishSave} /> }
		{ state.showAdminDialog && <AdminProjectCatalogModal catalogData={state.catalog} params={params} onChange={isDirty => updateState({ isDirty })} onClose={onDialogClose}
			onSave={onAdminDialogSave} /> }
		{ state.showPreviewsDialog && <EditProjectCatalogGalleryModal catalogData={state.catalog} gallery={state.gallery} isInternal={isInternal} params={params} onSave={onPreviewsSave} onClose={onDialogClose} /> }
	</Page>;
}
